import httpClient from '@utils/httpClient';
import {createApiCall, fetchAllPages, getApiUrl} from '../utils';
import {SERVICE_NAMES} from '../constants';
import type {ApiCallOptions, ApiResponse} from '../types';
import type {
    PaginatedResponse,
    ApprovalView,
    ApprovalCreateRequest,
    ApprovalUpdateRequest,
    ApprovalSignRequest,
    ApprovalCloneResultView,
    CloneApprovalRequest,
    ApprovalListParams
} from './api.d';

export type {ApprovalView};

export const approvalApi = {
    getApprovals: (transactionId: string, documentId: string, params?: ApprovalListParams, options?: ApiCallOptions) => createApiCall<PaginatedResponse<ApprovalView>>(SERVICE_NAMES.APPROVAL, 'GET', `transactions/${transactionId}/documents/${documentId}/approvals`)({...options, params}),

    getApproval: (transactionId: string, documentId: string, approvalId: string, options?: ApiCallOptions) => createApiCall<ApprovalView>(SERVICE_NAMES.APPROVAL, 'GET', `transactions/${transactionId}/documents/${documentId}/approvals/${approvalId}`)({...options}),

    createApproval: (transactionId: string, documentId: string, data: ApprovalCreateRequest, options?: ApiCallOptions) => createApiCall<ApprovalView>(SERVICE_NAMES.APPROVAL, 'POST', `transactions/${transactionId}/documents/${documentId}/approvals`)({...options, data}),

    updateApproval: (transactionId: string, documentId: string, approvalId: string, data: ApprovalUpdateRequest, options?: ApiCallOptions) => createApiCall<ApprovalView>(SERVICE_NAMES.APPROVAL, 'PUT', `transactions/${transactionId}/documents/${documentId}/approvals/${approvalId}`)({...options, data}),

    deleteApproval: (transactionId: string, documentId: string, approvalId: string, options?: ApiCallOptions) => createApiCall<void>(SERVICE_NAMES.APPROVAL, 'DELETE', `transactions/${transactionId}/documents/${documentId}/approvals/${approvalId}`)({...options}),

    signApproval: async (transactionId: string, documentId: string, approvalId: string, data?: ApprovalSignRequest, options?: ApiCallOptions): Promise<ApiResponse<ApprovalView>> => {
        const endpoint = `transactions/${transactionId}/documents/${documentId}/approvals/${approvalId}/sign`;
        const url = await getApiUrl(SERVICE_NAMES.APPROVAL, endpoint);

        const formData = new FormData();

        if (data?.signImage) {
            const {signImage} = data;
            formData.append('signImage', signImage);
        }

        if (data?.signedText) {
            formData.append('signedText', data.signedText);
        }

        const config: any = {
            method: 'put',
            url,
            data: formData,
            headers: {
                ...options?.headers
            }
        };

        if (options?.timeout) {
            config.timeout = options.timeout;
        }

        const response = await httpClient(config);
        return response.data;
    },

    undoApproval: (transactionId: string, documentId: string, approvalId: string, options?: ApiCallOptions) => createApiCall<ApprovalView>(SERVICE_NAMES.APPROVAL, 'PUT', `transactions/${transactionId}/documents/${documentId}/approvals/${approvalId}/undo`)({...options}),

    cloneApproval: (templateId: string, templateDocumentId: string, data: CloneApprovalRequest, options?: ApiCallOptions) => createApiCall<ApprovalCloneResultView>(SERVICE_NAMES.APPROVAL, 'POST', `templates/${templateId}/documents/${templateDocumentId}/clone`)({...options, data}),

    getAllApprovals: (transactionId: string, documentId: string, params?: Omit<ApprovalListParams, 'page'>, options?: ApiCallOptions) => fetchAllPages(
        (paginationParams: ApprovalListParams, opts?: ApiCallOptions) => approvalApi.getApprovals(transactionId, documentId, paginationParams, opts),
        params || {},
        options
    ),

    getApprovalsByRecipientId: (transactionId: string, documentId: string, recipientId: string, params?: ApprovalListParams, options?: ApiCallOptions) => createApiCall<PaginatedResponse<ApprovalView>>(SERVICE_NAMES.APPROVAL, 'GET', `transactions/${transactionId}/documents/${documentId}/recipients/${recipientId}`)({...options, params}),

    getApprovalsForTransaction: (transactionId: string, options?: ApiCallOptions) => createApiCall<ApprovalView[]>(SERVICE_NAMES.APPROVAL, 'GET', `transactions/${transactionId}/approvals`)({...options}),

    getOpenApprovalsForRecipientInDocument: (transactionId: string, documentId: string, recipientId: string, options?: ApiCallOptions) => createApiCall<ApprovalView[]>(SERVICE_NAMES.APPROVAL, 'GET', `transactions/${transactionId}/documents/${documentId}/recipients/${recipientId}/approvals`)({...options}),

    getAllApprovalsByRecipientId: (transactionId: string, documentId: string, recipientId: string, params?: Omit<ApprovalListParams, 'page'>, options?: ApiCallOptions) => fetchAllPages(
        (paginationParams: ApprovalListParams, opts?: ApiCallOptions) => approvalApi.getApprovalsByRecipientId(transactionId, documentId, recipientId, paginationParams, opts),
        params || {},
        options
    ).then((approvals) => approvals)
};
